import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { 
  Shield, LogOut, Users, Calendar, ClipboardList, 
  DollarSign, Mail, FileText, Settings, LayoutDashboard,
  Menu, X, Palette, Award, BarChart3, GraduationCap, Image, Images, ImagePlus
} from 'lucide-react';

/**
 * AdminNav - Global admin navigation component
 * This component provides consistent navigation across ALL admin pages.
 * DO NOT create page-specific navigation - use this component only.
 */

const navItems = [
  { path: '/admin/workshops', label: 'Workshops', icon: Palette },
  { path: '/admin/kids-classes', label: 'Kids Classes', icon: GraduationCap },
  { path: '/admin/exhibitions', label: 'Exhibitions', icon: Image },
  { path: '/admin/gallery', label: 'Gallery', icon: Images },
  { path: '/admin/members', label: 'Members', icon: Users },
  { path: '/admin/registrations', label: 'Registrations', icon: ClipboardList },
  { path: '/admin/subscribers', label: 'Subscribers', icon: Mail },
  { path: '/admin/contact-analytics', label: 'Contact Analytics', icon: BarChart3 },
  { path: '/admin/content', label: 'Programs', icon: FileText },
  { path: '/admin/artist-of-month', label: 'Artist of Month', icon: Award },
  { path: '/admin/media', label: 'Media', icon: Images },
  { path: '/admin/recover-artwork', label: 'Recover Artwork', icon: ImagePlus },
];

export const AdminNav = ({ title, subtitle }) => {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <>
      {/* Fixed Admin Navigation Bar */}
      <nav className="bg-stone-900 text-white sticky top-0 z-40 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between min-h-14 py-1 min-w-0">
            {/* Left: Logo/Brand + Nav */}
            <div className="flex items-center min-w-0 flex-1">
              <Link to="/admin" className="flex items-center gap-2 text-cafa-gold hover:text-cafa-gold-light transition-colors flex-shrink-0">
                <Shield size={20} />
                <span className="font-semibold hidden sm:inline">Admin</span>
              </Link>
              
              {/* Desktop Navigation — full row at lg+, hamburger below to keep all links reachable at ~900px */}
              <div className="hidden lg:flex flex-wrap items-center gap-1 ml-4 flex-1 min-w-0">
                {navItems.map((item) => (
                  <Link 
                    key={item.path} 
                    to={item.path}
                    className={`
                      flex-shrink-0 px-2.5 py-2 rounded-md text-xs font-medium transition-colors whitespace-nowrap
                      ${isActive(item.path) 
                        ? 'bg-stone-800 text-white' 
                        : 'text-stone-300 hover:bg-stone-800 hover:text-white'}
                    `}
                  >
                    <span className="flex items-center gap-1.5">
                      <item.icon size={14} />
                      {item.label}
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Right: View Site + Logout — always visible, flex-shrink-0 */}
            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
              <Link 
                to="/" 
                className="text-stone-400 hover:text-white text-sm hidden sm:block transition-colors"
              >
                View Site
              </Link>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleLogout} 
                className="text-stone-300 hover:text-white hover:bg-stone-800"
                aria-label="Log out"
              >
                <LogOut size={16} className="mr-1" /> 
                <span className="hidden sm:inline">Logout</span>
              </Button>
              
              {/* Mobile / mid-width menu button — visible below lg (covers tablets and ~900px window) */}
              <Button 
                variant="ghost" 
                size="sm"
                className="lg:hidden text-stone-300 hover:text-white"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={mobileMenuOpen ? 'Close admin menu' : 'Open admin menu'}
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu — also used at tablet/~900px widths */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-stone-800 border-t border-stone-700">
            <div className="px-4 py-3 space-y-1">
              {navItems.map((item) => (
                <Link 
                  key={item.path} 
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`
                    block px-3 py-2 rounded-md text-sm font-medium transition-colors
                    ${isActive(item.path) 
                      ? 'bg-stone-700 text-white' 
                      : 'text-stone-300 hover:bg-stone-700 hover:text-white'}
                  `}
                >
                  <span className="flex items-center gap-3">
                    <item.icon size={18} />
                    {item.label}
                  </span>
                </Link>
              ))}
              <div className="border-t border-stone-700 pt-2 mt-2">
                <Link 
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2 text-sm text-stone-400 hover:text-white"
                >
                  ← Back to Site
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Page Header */}
      {title && (
        <section className="bg-stone-800 text-white py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 text-cafa-gold text-sm mb-1">
              <Shield size={16} /> Admin Area
            </div>
            <h1 className="text-2xl sm:text-3xl font-serif">{title}</h1>
            {subtitle && <p className="text-stone-400 mt-1">{subtitle}</p>}
          </div>
        </section>
      )}
    </>
  );
};

/**
 * AdminLayout - Wrapper component for all admin pages
 * Provides consistent navigation and layout structure
 */
export const AdminLayout = ({ children, title, subtitle }) => {
  const { isAdmin, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!loading) {
      if (!isAuthenticated) {
        navigate('/members/login');
      } else if (!isAdmin) {
        navigate('/members');
      }
    }
  }, [loading, isAuthenticated, isAdmin, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafa-gold mx-auto"></div>
          <p className="mt-4 text-stone-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <AdminNav title={title} subtitle={subtitle} />
      <main>
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
